import classNames from "classnames";
import PropTypes from "prop-types";
import React from "react";
import { Row, Col } from "shards-react";
import { parseDate, ISO8601_week_no } from "../../backend/common";
import CardProbabilities from "./CardProbabilities";

import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";

import ChartDataLabels from "chartjs-plugin-datalabels";
import { Line } from "react-chartjs-2";
import { Card, CardBody } from "shards-react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

Tooltip.positioners.bottom = function (items) {
  const pos = Tooltip.positioners.average(items);

  // Happens when nothing is found
  if (pos === false) {
    return false;
  }

  const chart = this.chart;

  return {
    x: pos.x,
    y: chart.chartArea.bottom,
    xAlign: "center",
    yAlign: "bottom",
  };
};

class SmallStats extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      chartOptions: this.props.chartOptions,
      isMobile: this.checkIfMobile(),
    };
  }

  addWeeks(weeks, date = new Date()) {
    date.setDate(date.getDate() + weeks * 7);
    return date;
  }

  // Helper function to find last non-undefined item
  getLastNonUndefined(arr) {
    if (!arr) return null;
    for (let i = arr.length - 1; i >= 0; i--) {
      if (arr[i] !== undefined) {
        return { item: arr[i], index: i };
      }
    }
    return null;
  }

  // Check if device is mobile
  checkIfMobile() {
    return window.innerWidth <= 768; // You can adjust this breakpoint as needed
  }

  // Handle window resize
  handleResize = () => {
    const isMobile = this.checkIfMobile();
    if (this.state.isMobile !== isMobile) {
      this.setState({
        isMobile,
        chartOptions: this.createChartOptions(isMobile),
      });
    }
  };

  // Create a method to generate chart options
  createChartOptions(isMobile = this.state?.isMobile) {
    return {
      ...{
        layout: {
          padding: {
            left: 1,
            right: 1,
            top: 1,
            bottom: 1,
          },
        },
        maintainAspectRatio: false,
        responsive: true,
        hover: {
          mode: "index",
          intersect: false,
        },
        interaction: {
          mode: "index",
          intersect: false,
        },
        // Global font settings
        font: {
          size: 13,
          family: "'Helvetica Neue', 'Helvetica', 'Arial', sans-serif",
        },
        plugins: {
          tooltip: {
            mode: "index",
            intersect: false,
            position: "bottom",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            titleColor: "white",
            bodyColor: "white",
            borderColor: "rgba(255, 255, 255, 0.1)",
            borderWidth: 1,
            cornerRadius: 6,
            displayColors: true,
            padding: 10,
            titleFont: {
              size: 15,
              weight: "bold",
            },
            bodyFont: {
              size: 14,
            },
          },
          annotation: {
            annotations: {
              line1: {
                type: "line",
                yMin: 0,
                yMax: 0,
                borderColor: "rgb(170, 170, 170)",
                borderWidth: 1,
                z: 0,
              },
            },
          },
          legend: {
            display: true,
            position: "bottom",
            labels: {
              font: {
                size: 14,
              },
              filter: function (legendItem, chartData) {
                return !legendItem.text.endsWith("0");
              },
            },
          },
          datalabels: {
            backgroundColor: function (context) {
              return context.dataset.backgroundColor;
            },
            display: function (context) {
              const dataPointsCount = context.chart.data.labels?.length || 0;
              if (dataPointsCount >= 20) {
                return false;
              }
              return context.dataset.display;
            },
            color: "black",
            align: function (context) {
              if (!context.dataset.other) {
                return context.dataset.align;
              }
              if (context.dataset.data[context.dataIndex] == null) {
                return "start";
              } else if (context.dataset.other[context.dataIndex] == null) {
                return "end";
              }
              if (
                context.dataset.data[context.dataIndex] >
                context.dataset.other[context.dataIndex]
              ) {
                return "end";
              } else if (
                context.dataset.data[context.dataIndex] <
                context.dataset.other[context.dataIndex]
              ) {
                return "start";
              } else {
                return context.dataset.align;
              }
            },
            anchor: function (context) {
              if (!context.dataset.other) {
                return "start";
              }
              if (context.dataset.data[context.dataIndex] == null) {
                return "start";
              } else if (context.dataset.other[context.dataIndex] == null) {
                return "end";
              }
              if (
                context.dataset.data[context.dataIndex] >
                context.dataset.other[context.dataIndex]
              ) {
                return "end";
              } else if (
                context.dataset.data[context.dataIndex] <
                context.dataset.other[context.dataIndex]
              ) {
                return "start";
              } else {
                return context.dataset.anchor;
              }
            },
            font: {
              size: 13,
              weight: "bold",
            },
          },
        },
        elements: {
          point: {
            radius: function (context) {
              const dataPointsCount = context.chart.data.labels?.length || 0;
              return dataPointsCount >= 20 ? 1 : 4;
            },
          },
          line: {
            tension: 0.3,
            spanGaps: false,
            fill: false,
            borderWidth: 2,
          },
        },
        scales: {
          x: {
            gridLines: true,
            ticks: {
              display: true,
              padding: 10,
              maxRotation: isMobile ? 90 : 0,
              minRotation: isMobile ? 90 : 0,
              font: {
                size: 13,
              },
            },
            position: "bottom",
          },
          y: {
            gridLines: {
              display: true,
              color: "rgba(102,102,102,0.48)",
            },
            ticks: {
              display: !isMobile,
              padding: 20,
              font: {
                size: 13,
              },
            },
            max: this.props.max,
            min: this.props.min,
            stacked: false,
          },
        },
      },
    };
  }

  componentDidMount() {
    // Add resize event listener
    window.addEventListener("resize", this.handleResize);

    this.setState({
      chartOptions: this.createChartOptions(),
    });
  }

  componentWillUnmount() {
    // Remove resize event listener
    window.removeEventListener("resize", this.handleResize);
  }

  componentDidUpdate(prevProps) {
    // Check if min, max props have changed, or if the number of data points changed
    const prevDataPointsCount = prevProps.chartData?.labels?.length || 0;
    const currentDataPointsCount = this.props.chartData?.labels?.length || 0;

    if (
      prevProps.min !== this.props.min ||
      prevProps.max !== this.props.max ||
      prevDataPointsCount !== currentDataPointsCount
    ) {
      this.setState({
        chartOptions: this.createChartOptions(),
      });
    }
  }

  render() {
    const {
      chartData,
      variation,
      label,
      precisionIA,
      precisionBinefar,
      thresholdPrecision,
      prediction4Weeks,
      pricesDiff4Weeks,
      dates,
    } = this.props;

    const cardClasses = classNames(
      "stats-small mb-3 mt-4",
      variation && `stats-small--${variation}`
    );

    const cardBodyClasses = classNames(
      variation === "1" ? "p-0 d-flex" : "p-0"
    );

    const dataFieldClasses = classNames(
      "stats-small__data",
      "d-flex",
      variation === "1" && "text-center",
      variation === "2" && "text-left"
    );

    const labelClasses = classNames(
      "stats-big__label",
      "text-uppercase",
      variation !== "1" && "mt-3"
    );

    // Get the last non-undefined prediction
    const lastPrediction = this.getLastNonUndefined(prediction4Weeks);

    return (
      <Card small className={cardClasses}>
        <Row className="justify-content-between m-0 mt-2 px-4">
          <span className={labelClasses}>{label}</span>
          {!this.checkIfMobile() && (
            <Row
              style={{
                paddingRight: "1rem",
                fontSize: "0.8rem",
              }}
            >
              {precisionIA && (
                <span
                  style={{
                    whiteSpace: "pre-line",
                    lineHeight: 0.7,
                    textAlign: "right",
                    marginBottom: "0.3rem",
                    color: "white",
                    paddingRight: "1rem",
                  }}
                  className={labelClasses}
                >
                  {`\nPrecisión [<${thresholdPrecision}€]
              \nIA: ${precisionIA}% ─┤
              \nBinéfar: ${
                isNaN(precisionBinefar) ? "--.--" : precisionBinefar
              }% ─┘`}
                </span>
              )}
              {lastPrediction && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "end",
                  }}
                >
                  <span>PREDICCIÓN A 4 SEMANAS</span>
                  <CardProbabilities
                    hideRealTrend={true}
                    currentWeek={ISO8601_week_no(
                      new Date(dates[lastPrediction.index])
                    )}
                    currentWeekPlus4={ISO8601_week_no(
                      this.addWeeks(4, new Date(dates[lastPrediction.index]))
                    )}
                    probs={lastPrediction.item}
                    maxProbsIndex={
                      lastPrediction.item
                        ? Object.keys(lastPrediction.item).reduce((a, b) =>
                            lastPrediction.item[a] > lastPrediction.item[b]
                              ? a
                              : b
                          )
                        : null
                    }
                  />
                </div>
              )}
            </Row>
          )}
        </Row>
        <CardBody className={cardBodyClasses}>
          <div
            style={{
              minHeight: "400px",
              maxHeight: "500px",
              padding: "0 10px",
            }}
          >
            <Line
              data={chartData}
              options={this.state.chartOptions}
              height={"100"}
            />
          </div>
          <div
            style={{
              overflowX: "auto",
              display: "block",
              WebkitOverflowScrolling: "touch",
              width: "100%",
            }}
          >
            <table
              className="table mb-0"
              style={{
                fontSize: "14px",
                color: "#5A6169",
                fontFamily:
                  "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif",
              }}
            >
              <thead className="bg-light">
                <tr>
                  <th>Titulo</th>
                  {dates?.map((value, index) => {
                    return (
                      <th key={index} scope="col" className="border-0">
                        {parseDate(value)}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody style={{ maxHeight: "500px", overflow: "scroll" }}>
                {chartData.datasets.map((dataset, index) => {
                  return (
                    <tr key={index}>
                      <td style={{ color: dataset.borderColor }}>
                        {dataset.label}
                      </td>
                      {dataset.data.map((value, valueIndex) => (
                        <td key={valueIndex}>{!value ? "--" : value}</td>
                      ))}
                    </tr>
                  );
                })}
                {prediction4Weeks && (
                  <tr>
                    <td style={{ color: "rgb(0, 153, 209)" }}>
                      Predicción 4 Semanas
                    </td>
                    {prediction4Weeks.map((probs, index) => {
                      const maxProbsIndex = probs
                        ? Object.keys(probs).reduce((a, b) =>
                            probs[a] > probs[b] ? a : b
                          )
                        : null;
                      return (
                        <td
                          key={index}
                          style={{
                            placeSelf: "center",
                            margin: "0 auto",
                          }}
                        >
                          {probs ? (
                            <CardProbabilities
                              currentWeek={ISO8601_week_no(
                                new Date(dates[index])
                              )}
                              currentWeekPlus4={ISO8601_week_no(
                                this.addWeeks(4, new Date(dates[index]))
                              )}
                              probs={probs}
                              maxProbsIndex={maxProbsIndex}
                              pricesDiff4Weeks={pricesDiff4Weeks[index]}
                            />
                          ) : (
                            "--"
                          )}
                        </td>
                      );
                    })}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    );
  }
}

SmallStats.propTypes = {
  /**
   * The Small Stats variation.
   */
  variation: PropTypes.string,
  /**
   * The label.
   */
  label: PropTypes.string,
  /**
   * The value.
   */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * The percentage number or string.
   */
  percentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Whether is a value increase, or not.
   */
  increase: PropTypes.bool,
  /**
   * The Chart.js configuration object.
   */
  chartConfig: PropTypes.object,
  /**
   * The Chart.js options object.
   */
  chartOptions: PropTypes.object,
  /**
   * The chart data.
   */
  chartData: PropTypes.array.isRequired,
  /**
   * The chart labels.
   */
  chartLabels: PropTypes.array,
  /**
   * The minimum value for the y-axis.
   */
  min: PropTypes.number,
  /**
   * The maximum value for the y-axis.
   */
  max: PropTypes.number,
};

SmallStats.defaultProps = {
  increase: true,
  values: 0,
  label: "Label",
  chartOptions: {
    ...{
      maintainAspectRatio: true,
      responsive: true,
      legend: {
        display: true,
        position: "bottom",
      },
      hover: {
        mode: "nearest",
        intersect: false,
      },
      tooltips: {
        custom: false,
        mode: "nearest",
        intersect: false,
      },
      elements: {
        point: {
          radius: 2,
        },
        line: {
          tension: 0.33,
        },
      },
    },
  },
  chartData: [],
  chartLabels: [],
};

export default SmallStats;
